import {
  ArrowBackIosNew,
  ArrowDownward,
  ArrowForward,
  ArrowForwardIos,
  ArrowUpward,
  Edit,
  FileDownload,
  KeyboardArrowDown,
} from '@mui/icons-material';
import {
  Box,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, ButtonVariants } from '@/components';
import { Modals, Routes } from '@/constants';
import { Link, LinkVariants } from '@/containers';
import { AnalyticsService, EVENTS } from '@/services';
import {
  downloadCertificate,
  selectUserCompliance,
  uiActions,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { Certificate } from '@/types';
import { createRouteWithParams } from '@/utils';

import { EthicsPill } from './EthicsPill';

const Columns = {
  CPD: [
    { name: 'title', label: 'Title', width: '46%' },
    { name: 'deliveryMethod', label: 'Delivery Method', width: '22%' },
    { name: 'credit', label: 'Hours', width: '9%' },
    { name: 'dateCompleted', label: 'Date', width: '11.5%' },
    { name: 'certificate', label: 'Download / Edit', width: '11.5%' },
  ],
  CPE: [
    { name: 'title', label: 'Title', width: '37%' },
    { name: 'deliveryMethod', label: 'Delivery Method', width: '17%' },
    { name: 'fieldsOfStudy', label: 'Fields of Study', width: '14%' },
    { name: 'credit', label: 'Credits', width: '9%' },
    { name: 'dateCompleted', label: 'Date', width: '11.5%' },
    { name: 'certificate', label: 'Download / Edit', width: '11.5%' },
  ],
} as const;

type SortableColumn = Exclude<
  (typeof Columns)[keyof typeof Columns][number]['name'],
  'certificate'
>;

const getSortValue = (certificate: Certificate, columnName: SortableColumn) => {
  return columnName === 'fieldsOfStudy'
    ? (certificate.fieldsOfStudy?.[0]?.name ?? '')
    : certificate[columnName];
};

export const CertificatesTable = ({
  certificates,
  filters,
}: {
  certificates: Certificate[];
  filters: {
    type?: 'all' | 'lumiq' | 'custom';
    searchTerm?: string;
  };
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const isLgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const complianceType = useAppSelector(selectUserCompliance);
  const [sortBy, setSortBy] = useState<{
    currentColumn: SortableColumn;
    sortByDirections: Record<SortableColumn, 'asc' | 'desc'>;
  }>({
    currentColumn: 'dateCompleted',
    sortByDirections: {
      dateCompleted: 'desc',
      deliveryMethod: 'asc',
      title: 'asc',
      credit: 'asc',
      fieldsOfStudy: 'asc',
    },
  });
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = useMemo(() => {
    return Math.ceil(certificates.length / 10); // Assuming 10 certificates per page
  }, [certificates.length]);

  const sortedCertificates = useMemo(() => {
    return [...certificates].sort((a, b) => {
      const aValue = getSortValue(a, sortBy.currentColumn);
      const bValue = getSortValue(b, sortBy.currentColumn);
      const direction = sortBy.sortByDirections[sortBy.currentColumn];
      const aIsNullish = aValue === null || aValue === undefined;
      const bIsNullish = bValue === null || bValue === undefined;

      if (aIsNullish && bIsNullish) return 0;

      if (direction === 'asc') {
        if (aIsNullish) return 1;
        if (bIsNullish) return -1;
        if (aValue < bValue) return -1;
        if (aValue > bValue) return 1;
        return 0;
      } else {
        if (aIsNullish) return -1;
        if (bIsNullish) return 1;
        if (aValue > bValue) return -1;
        if (aValue < bValue) return 1;
        return 0;
      }
    });
  }, [certificates, sortBy]);

  const setSortByColumn = useCallback(
    (columnName: SortableColumn) => {
      if (sortBy.currentColumn === columnName) {
        setSortBy({
          currentColumn: columnName,
          sortByDirections: {
            ...sortBy.sortByDirections,
            [columnName]:
              sortBy.sortByDirections[columnName] === 'asc' ? 'desc' : 'asc',
          },
        });
        AnalyticsService.logEvent(EVENTS.TRACKER_EVENTS.SORT_CHANGED, {
          column: columnName,
          direction:
            sortBy.sortByDirections[columnName] === 'asc' ? 'desc' : 'asc',
        });
      } else {
        setSortBy({
          currentColumn: columnName,
          sortByDirections: sortBy.sortByDirections,
        });
        AnalyticsService.logEvent(EVENTS.TRACKER_EVENTS.SORT_CHANGED, {
          column: columnName,
          direction: sortBy.sortByDirections[columnName],
        });
      }
      setCurrentPage(1);
    },
    [sortBy, setSortBy, setCurrentPage],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, setCurrentPage]);

  return isMobile ? (
    <Box
      sx={{
        width: '100%',
        borderRadius: 2.5,
        border: theme => `1px solid ${theme.palette.grey100}80`,
        overflow: 'hidden',
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1,
          px: 1.5,
          bgcolor: theme => `${theme.palette.secondary.medium}66`,
          borderBottom: theme => `1px solid ${theme.palette.grey100}80`,
        }}>
        <Typography variant="body3SemiBold">Certificates</Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1.5,
          }}>
          <Typography variant="body3SemiBold">Date</Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              height: 22.5,
              bgcolor:
                sortBy.currentColumn === 'dateCompleted'
                  ? 'secondary.main'
                  : 'secondary.medium',
              borderRadius: 5.5,
              cursor: 'pointer',
              userSelect: 'none',
              px: 0.75,
            }}
            onClick={() => {
              setSortByColumn('dateCompleted');
            }}>
            <ArrowUpward
              sx={{
                width: 12,
                height: 12,
                color:
                  sortBy.currentColumn === 'dateCompleted'
                    ? sortBy.sortByDirections['dateCompleted'] === 'asc'
                      ? 'white'
                      : '#FFFFFF80'
                    : 'secondary.main',
              }}
            />
            <ArrowDownward
              sx={{
                width: 12,
                height: 12,
                color:
                  sortBy.currentColumn === 'dateCompleted'
                    ? sortBy.sortByDirections['dateCompleted'] === 'desc'
                      ? 'white'
                      : '#FFFFFF80'
                    : 'secondary.main',
              }}
            />
          </Box>
        </Box>
      </Box>
      {sortedCertificates.length > 0 &&
        sortedCertificates
          .slice((currentPage - 1) * 10, currentPage * 10)
          .map((certificate, index) => (
            <Box
              key={certificate.certificateId}
              sx={{
                py: 0.25,
              }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  bgcolor: index % 2 === 0 ? 'white' : 'sand100',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    py: 1.25,
                    px: 1.5,
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                    }}>
                    {certificate.ethicsCredit > 0 &&
                      complianceType === 'CPD' && (
                        <Box sx={{ width: 'fit-content' }}>
                          <EthicsPill />
                        </Box>
                      )}
                    {certificate.type === 'lumiq' ? (
                      <Link
                        textVariant="body3SemiBold"
                        variant={LinkVariants.CLASSIC}
                        to={createRouteWithParams(Routes.EPISODE, {
                          episodeId: certificate.episodeId!,
                        })}>
                        {certificate.title.split('LumiQ - ')[1]}
                      </Link>
                    ) : (
                      <Typography variant="body3SemiBold">
                        {certificate.title}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption1Bold" color="grey175">
                    {certificate.type === 'lumiq'
                      ? 'LumiQ'
                      : (certificate.provider ??
                        certificate.otherProvider ??
                        'Other')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 1.25,
                      mt: 0.5,
                    }}>
                    <Typography variant="caption1">
                      {new Date(certificate.dateCompleted).toLocaleDateString(
                        'en-US',
                        {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        },
                      )}
                    </Typography>
                    <Box
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                      }}
                    />
                    <Typography variant="caption1">
                      {Math.round(certificate.credit * 100) / 100}{' '}
                      {complianceType === 'CPD' ? 'Hours' : 'Credits'}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    mr: 1.5,
                  }}>
                  {certificate.type === 'custom' && (
                    <Edit
                      sx={{
                        width: 16,
                        height: 16,
                        color: 'primary.main',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        dispatch(
                          uiActions.setActiveModal({
                            name: Modals.TRACKER_ADD_EDIT,
                            params: {
                              credit: certificate,
                            },
                          }),
                        );
                        AnalyticsService.logEvent(
                          EVENTS.TRACKER_EVENTS.EXTERNAL_CERTIFICATE_EDITED,
                          {
                            certificate_name: certificate.title,
                          },
                        );
                      }}
                    />
                  )}
                  <FileDownload
                    sx={{
                      width: 16,
                      height: 16,
                      color: 'primary.main',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      dispatch(
                        downloadCertificate({
                          certificateId: certificate.certificateId,
                        }),
                      );
                      AnalyticsService.logEvent(
                        EVENTS.TRACKER_EVENTS.CERTIFICATE_DOWNLOADED,
                        {
                          episode_id: certificate.episodeId ?? '',
                          episode_name: certificate.title,
                          source:
                            certificate.type === 'lumiq' ? 'LumiQ' : 'External',
                        },
                      );
                    }}
                  />
                </Box>
              </Box>
            </Box>
          ))}
      {sortedCertificates.length === 0 && (
        <Box
          sx={{
            width: '100%',
            p: 2,
            height: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'sand100',
            borderBottom: theme => `1px solid ${theme.palette.grey100}80`,
          }}>
          <Typography variant="h4" textAlign="center">
            {filters.type === 'custom'
              ? `No external ${complianceType === 'CPD' ? 'certificates' : 'credits'} found.`
              : filters.searchTerm
                ? `No search results found for "${filters.searchTerm.length > 30 ? filters.searchTerm.substring(0, 30) + '...' : filters.searchTerm}".`
                : 'No certificates found for this date range.'}
          </Typography>
          <Typography
            variant="body3"
            color="grey150"
            textAlign="center"
            sx={{ mt: 1 }}>
            To add certificates to your tracker, you can finish a new episode or
            log your external hours.
          </Typography>
          <Button
            variant={ButtonVariants.SECONDARY}
            colorOverride="secondary.main"
            fullWidth={false}
            skinny={true}
            onClick={() => {
              if (filters.type === 'custom') {
                dispatch(
                  uiActions.setActiveModal({
                    name: Modals.TRACKER_ADD_EDIT,
                  }),
                );
              } else {
                navigate({
                  pathname: Routes.LIBRARY,
                });
              }
            }}
            sx={{ mt: 3, color: 'primary.main' }}
            label={
              filters.type === 'custom' ? 'Add Certificates' : 'Browse Episodes'
            }
          />
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          height: 40,
          bgcolor: 'white',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 4,
          borderTop: theme => `1px solid ${theme.palette.grey100}80`,
        }}>
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            border: theme => `1px solid ${theme.palette.grey150}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover': {
              opacity: 0.7,
            },
          }}
          onClick={() => {
            if (currentPage > 1) {
              setCurrentPage(currentPage - 1);
            }
          }}>
          <ArrowBackIosNew
            sx={{
              width: 13,
              height: 13,
              color: theme => theme.palette.grey150,
            }}
          />
        </Box>
        <Box
          sx={{
            minWidth: 150,
            height: 20,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <Typography variant="body3">
            <strong>
              {sortedCertificates.length > 0 ? (currentPage - 1) * 10 + 1 : 0} -{' '}
              {Math.min(currentPage * 10, sortedCertificates?.length ?? 0)}
            </strong>{' '}
            of <strong>{sortedCertificates?.length ?? 0}</strong> certificates
          </Typography>
        </Box>
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            border: theme => `1px solid ${theme.palette.grey150}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover': {
              opacity: 0.7,
            },
          }}
          onClick={() => {
            if (currentPage * 10 < (sortedCertificates?.length ?? 0)) {
              setCurrentPage(currentPage + 1);
            }
          }}>
          <ArrowForwardIos
            sx={{
              width: 13,
              height: 13,
              color: theme => theme.palette.grey150,
            }}
          />
        </Box>
      </Box>
    </Box>
  ) : (
    <Box
      sx={{
        width: '100%',
        borderRadius: 2.5,
        border: theme => `1px solid ${theme.palette.grey100}80`,
        overflow: 'hidden',
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
        }}>
        {Columns[complianceType].map(column => (
          <Box
            key={column.name}
            sx={{
              width: column.width,
              py: 1,
              px: 1.5,
              bgcolor: theme => `${theme.palette.secondary.medium}66`,
              borderBottom: theme => `1px solid ${theme.palette.grey100}80`,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.5,
            }}>
            <Typography variant="body3SemiBold">{column.label}</Typography>
            {column.name !== 'certificate' && (
              <Box
                sx={{
                  display:
                    isLgDown && column.name !== 'title' ? 'none' : 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  height: 22.5,
                  bgcolor:
                    sortBy.currentColumn === column.name
                      ? 'secondary.main'
                      : 'secondary.medium',
                  borderRadius: 5.5,
                  cursor: 'pointer',
                  userSelect: 'none',
                  px: 0.75,
                }}
                onClick={() => {
                  setSortByColumn(column.name);
                }}>
                {column.name === 'title' || column.name === 'fieldsOfStudy' ? (
                  <>
                    <Typography
                      variant="caption2Bold"
                      sx={{
                        color:
                          sortBy.currentColumn === column.name
                            ? 'white'
                            : 'secondary.main',
                      }}>
                      {sortBy.sortByDirections[column.name] === 'asc'
                        ? 'A'
                        : 'Z'}
                    </Typography>
                    <ArrowForward
                      sx={{
                        width: 12,
                        height: 12,
                        color:
                          sortBy.currentColumn === column.name
                            ? 'white'
                            : 'secondary.main',
                      }}
                    />
                    <Typography
                      variant="caption2Bold"
                      sx={{
                        color:
                          sortBy.currentColumn === column.name
                            ? 'white'
                            : 'secondary.main',
                      }}>
                      {sortBy.sortByDirections[column.name] === 'asc'
                        ? 'Z'
                        : 'A'}
                    </Typography>
                  </>
                ) : (
                  <>
                    <ArrowUpward
                      sx={{
                        width: 12,
                        height: 12,
                        color:
                          sortBy.currentColumn === column.name
                            ? sortBy.sortByDirections[column.name] === 'asc'
                              ? 'white'
                              : '#FFFFFF80'
                            : 'secondary.main',
                      }}
                    />
                    <ArrowDownward
                      sx={{
                        width: 12,
                        height: 12,
                        color:
                          sortBy.currentColumn === column.name
                            ? sortBy.sortByDirections[column.name] === 'desc'
                              ? 'white'
                              : '#FFFFFF80'
                            : 'secondary.main',
                      }}
                    />
                  </>
                )}
              </Box>
            )}
          </Box>
        ))}
      </Box>
      {sortedCertificates.length > 0 &&
        sortedCertificates
          .slice((currentPage - 1) * 10, currentPage * 10)
          .map((certificate, index) => (
            <Box
              key={certificate.certificateId}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                bgcolor: index % 2 === 0 ? 'white' : 'sand100',
                mt: 0.5,
              }}>
              {Columns[complianceType].map(column => (
                <Box
                  key={`${certificate.certificateId}-${column.name}`}
                  sx={{
                    width: column.width,
                    display: 'flex',
                    alignItems: 'center',
                    py: 2.5,
                    px: 1.25,
                  }}>
                  {column.name === 'title' && (
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          minHeight: 19,
                          alignItems: 'center',
                          gap: 2.5,
                        }}>
                        {certificate.type === 'lumiq' ? (
                          <Box
                            sx={{
                              '&:hover': {
                                textDecoration: 'underline',
                                color: 'secondary.main',
                              },
                            }}>
                            <Link
                              textVariant="body3SemiBold"
                              variant={LinkVariants.PLAIN}
                              to={createRouteWithParams(Routes.EPISODE, {
                                episodeId: certificate.episodeId!,
                              })}>
                              {certificate.title.split('LumiQ - ')[1]}
                            </Link>
                          </Box>
                        ) : (
                          <Typography variant="body3SemiBold">
                            {certificate.title}
                          </Typography>
                        )}
                        {certificate.ethicsCredit > 0 &&
                          complianceType === 'CPD' && <EthicsPill />}
                      </Box>
                      <Typography variant="caption1Bold" color="grey175">
                        {certificate.type === 'lumiq'
                          ? 'LumiQ'
                          : (certificate.provider ??
                            certificate.otherProvider ??
                            'Other')}
                      </Typography>
                    </Box>
                  )}
                  {column.name === 'deliveryMethod' && (
                    <Typography variant="body3">
                      {certificate.type === 'lumiq'
                        ? 'QAS Self-Study'
                        : (certificate.deliveryMethod ?? 'N/A')}
                    </Typography>
                  )}
                  {column.name === 'fieldsOfStudy' && (
                    <Typography variant="body3">
                      {certificate.fieldsOfStudy?.[0]?.name}
                    </Typography>
                  )}
                  {column.name === 'credit' && (
                    <Typography variant="body3">
                      {Math.round(certificate.credit * 100) / 100}
                    </Typography>
                  )}
                  {column.name === 'dateCompleted' && (
                    <Typography variant="body3">
                      {new Date(certificate.dateCompleted).toLocaleDateString(
                        'en-US',
                        {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        },
                      )}
                    </Typography>
                  )}
                  {column.name === 'certificate' && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                      }}>
                      {certificate.type === 'custom' && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1.5,
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': {
                              opacity: 0.7,
                            },
                          }}
                          onClick={() => {
                            if (certificate.type === 'lumiq') {
                              navigate({
                                pathname: createRouteWithParams(
                                  Routes.EPISODE,
                                  {
                                    episodeId: certificate.episodeId!,
                                  },
                                ),
                              });
                            } else {
                              dispatch(
                                uiActions.setActiveModal({
                                  name: Modals.TRACKER_ADD_EDIT,
                                  params: {
                                    credit: certificate,
                                  },
                                }),
                              );
                              AnalyticsService.logEvent(
                                EVENTS.TRACKER_EVENTS
                                  .EXTERNAL_CERTIFICATE_EDITED,
                                {
                                  certificate_name: certificate.title,
                                },
                              );
                            }
                          }}>
                          <Edit sx={{ width: 16, height: 16 }} />
                          {!isMdDown && (
                            <Typography variant="body3">Edit</Typography>
                          )}
                        </Box>
                      )}
                      <Tooltip
                        disableHoverListener={!!certificate.fileUri}
                        title="No file available for download">
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1.5,
                            cursor: certificate.fileUri ? 'pointer' : 'default',
                            userSelect: 'none',
                            opacity: certificate.fileUri ? 1 : 0.5,
                            '&:hover': {
                              opacity: certificate.fileUri ? 0.7 : 0.5,
                            },
                          }}
                          onClick={() => {
                            if (certificate.fileUri) {
                              dispatch(
                                downloadCertificate({
                                  certificateId: certificate.certificateId,
                                }),
                              );
                              AnalyticsService.logEvent(
                                EVENTS.TRACKER_EVENTS.CERTIFICATE_DOWNLOADED,
                                {
                                  episode_id: certificate.episodeId ?? '',
                                  episode_name: certificate.title,
                                  source:
                                    certificate.type === 'lumiq'
                                      ? 'LumiQ'
                                      : 'External',
                                },
                              );
                            }
                          }}>
                          <FileDownload sx={{ width: 16, height: 16 }} />
                          {!isMdDown && (
                            <Typography variant="body3">Download</Typography>
                          )}
                        </Box>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          ))}
      {sortedCertificates.length === 0 && (
        <Box
          sx={{
            width: '100%',
            height: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'sand100',
            borderBottom: theme => `1px solid ${theme.palette.grey100}80`,
          }}>
          <Typography variant="h3" textAlign="center">
            {filters.type === 'custom'
              ? `No external ${complianceType === 'CPD' ? 'certificates' : 'credits'} found.`
              : filters.searchTerm
                ? `No search results found for "${filters.searchTerm.length > 30 ? filters.searchTerm.substring(0, 30) + '...' : filters.searchTerm}".`
                : 'No certificates found for this date range.'}
          </Typography>
          <Typography
            variant="body3"
            color="grey150"
            textAlign="center"
            sx={{ mt: 1 }}>
            To add certificates to your tracker, you can finish a new episode or
            log your external hours.
          </Typography>
          <Button
            variant={ButtonVariants.SECONDARY}
            colorOverride="secondary.main"
            fullWidth={false}
            skinny={false}
            onClick={() => {
              if (filters.type === 'custom') {
                dispatch(
                  uiActions.setActiveModal({
                    name: Modals.TRACKER_ADD_EDIT,
                  }),
                );
                return;
              } else {
                navigate({
                  pathname: Routes.LIBRARY,
                });
              }
            }}
            sx={{ mt: 3, color: 'primary.main' }}
            label={
              filters.type === 'custom' ? 'Add Certificates' : 'Browse Episodes'
            }
          />
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          height: 60,
          bgcolor: 'white',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 3,
          borderTop: theme => `1px solid ${theme.palette.grey100}80`,
        }}>
        <Typography variant="body3">
          <strong>
            {sortedCertificates.length > 0 ? (currentPage - 1) * 10 + 1 : 0} -{' '}
            {Math.min(currentPage * 10, sortedCertificates?.length ?? 0)}
          </strong>{' '}
          of <strong>{sortedCertificates?.length ?? 0}</strong> certificates
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: currentPage === 1 ? 'auto' : 'pointer',
              userSelect: 'none',
              '&:hover': { opacity: currentPage === 1 ? 1 : 0.7 },
              bgcolor: 'grey50',
            }}
            onClick={() => {
              if (currentPage > 1) {
                setCurrentPage(currentPage - 1);
              }
            }}>
            <KeyboardArrowDown
              sx={{
                color: currentPage === 1 ? 'grey100' : 'grey200',
                width: 28,
                height: 28,
                transform: 'rotate(90deg)',
              }}
            />
          </Box>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: currentPage === totalPages ? 'auto' : 'pointer',
              userSelect: 'none',
              '&:hover': { opacity: currentPage === totalPages ? 1 : 0.7 },
              bgcolor: 'grey50',
            }}
            onClick={() => {
              if (currentPage < totalPages) {
                setCurrentPage(currentPage + 1);
              }
            }}>
            <KeyboardArrowDown
              sx={{
                color: currentPage === totalPages ? 'grey100' : 'grey200',
                width: 28,
                height: 28,
                transform: 'rotate(-90deg)',
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
